/**
 * 通知点击 → 落到正确页面。
 *
 * 两个真实失败：
 *
 * 1. **冷启动深链在 iOS 上会丢路径**。`clients.openWindow('/chat/7')` 在 iOS PWA 上常常落在
 *    start_url，目标路径丢失，而且完全静默。对策：SW 先把目标写进 Cache，应用启动时读出来
 *    自己路由。（Cache 是唯一在 SW 和页面之间都可用、且冷启动能跨进程活下来的通道。）
 *
 * 2. **已有窗口时不能用 `client.navigate()`**：对不受当前 SW 控制的页面会抛错。统一走
 *    postMessage 让页面自己路由。
 *
 * 键名与 SW 侧共用同一个常量（本文件被两端 import），不存在改一边漏一边。
 */
import { SW_MSG, isSwMessage } from './messages.js';
/** 暂存目标路径的 Cache 键。Cache 的 key 必须是同源 URL，所以用一个不会与真实路由撞车的路径。 */
export const PENDING_NAV_KEY = '/__pwaship_pending_nav';
/**
 * 把目标路径写进 Cache。SW 侧在 notificationclick 里调。
 * 写不进去也不抛——退化成只依赖 postMessage / openWindow 那条路。
 */
export async function stashPendingNav(cacheName, path, now = Date.now()) {
    try {
        const cache = await caches.open(cacheName);
        const body = { path, ts: now };
        await cache.put(PENDING_NAV_KEY, new Response(JSON.stringify(body), { headers: { 'content-type': 'application/json' } }));
        return true;
    }
    catch {
        return false;
    }
}
/**
 * 应用启动时消费一次。
 *
 * ⚠️ **必须限定新鲜度**。不限的话，用户三天前点过的一条通知会在今天冷启动时把他弹到那个
 * 会话去——他会觉得应用疯了。默认只认 60 秒内的。
 *
 * ⚠️ 读完必须删。不删的话每次启动都跳一次。
 */
export async function consumePendingNav(cacheName, options = {}) {
    const { maxAgeMs = 60_000, now = Date.now() } = options;
    try {
        const cache = await caches.open(cacheName);
        const res = await cache.match(PENDING_NAV_KEY);
        if (!res)
            return null;
        await cache.delete(PENDING_NAV_KEY);
        const body = (await res.json());
        if (typeof body?.path !== 'string' || !body.path)
            return null;
        if (typeof body.ts !== 'number' || now - body.ts > maxAgeMs)
            return null;
        return body.path;
    }
    catch {
        // Cache 不可用（隐私模式）或内容损坏：当作没有待跳转，不影响启动
        return null;
    }
}
/** 监听 SW 发来的导航消息（已有窗口的情况）。返回取消函数。 */
export function onSwNavigate(handler) {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator))
        return () => { };
    const listener = (event) => {
        const data = event.data;
        if (!isSwMessage(data) || data.type !== SW_MSG.NAVIGATE)
            return;
        const path = data.path;
        if (typeof path === 'string' && path)
            handler(path);
    };
    navigator.serviceWorker.addEventListener('message', listener);
    return () => navigator.serviceWorker.removeEventListener('message', listener);
}
//# sourceMappingURL=nav.js.map